import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../../components/UI/Button';
import { ActionMenuItem } from '../../components/UI/ActionMenu';
import { ResponsiveEntityList } from '../../components/UI/ResponsiveEntityList';
import { Modal } from '../../components/UI/Modal';
import { useToast } from '../../components/UI/ToastProvider';
import { labelService } from '../../services/labelService';
import { Label } from '../../types/label';

const emptyForm = {
  name: '',
  description: '',
  color: '#2563eb',
  isActive: true,
};

export const LabelsPage: React.FC = () => {
  const { addToast } = useToast();
  const [labels, setLabels] = useState<Label[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingLabel, setEditingLabel] = useState<Label | null>(null);
  const [labelToDelete, setLabelToDelete] = useState<Label | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadLabels = useCallback(async () => {
    setLoading(true);
    try
    {
      const data = await labelService.getAll();
      setLabels(data);
    }
    catch
    {
      addToast('Erro ao carregar etiquetas', 'error');
    }
    finally
    {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    void loadLabels();
  }, [loadLabels]);

  const filtered = labels.filter(l => l.name.toLowerCase().includes(search.toLowerCase()));

  const openCreateModal = () => {
    setEditingLabel(null);
    setForm(emptyForm);
    setIsModalOpen(true);
  };

  const openEditModal = (label: Label) => {
    setEditingLabel(label);
    setForm({
      name: label.name,
      description: label.description ?? '',
      color: label.color ?? '#2563eb',
      isActive: label.isActive,
    });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim())
    {
      addToast('Informe o nome da etiqueta', 'error');
      return;
    }

    setSaving(true);
    try
    {
      if (editingLabel)
      {
        await labelService.update(editingLabel.id, {
          name: form.name.trim(),
          description: form.description || null,
          color: form.color,
          isActive: form.isActive,
        });
        addToast('Etiqueta atualizada', 'success');
      }
      else
      {
        await labelService.create({
          name: form.name.trim(),
          description: form.description || null,
          color: form.color,
          isActive: form.isActive,
        });
        addToast('Etiqueta criada', 'success');
      }

      setIsModalOpen(false);
      await loadLabels();
    }
    catch
    {
      addToast('Erro ao salvar etiqueta', 'error');
    }
    finally
    {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!labelToDelete)
    {
      return;
    }

    try
    {
      await labelService.delete(labelToDelete.id);
      addToast('Etiqueta removida', 'success');
      setIsDeleteModalOpen(false);
      setLabelToDelete(null);
      await loadLabels();
    }
    catch
    {
      addToast('Erro ao remover etiqueta', 'error');
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Etiquetas</h1>
          <p className="text-gray-600 text-sm mt-1">Classificação operacional de conversas</p>
        </div>
        <Button variant="primary" onClick={openCreateModal}>
          Nova etiqueta
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <input
          type="text"
          placeholder="Buscar etiqueta..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-500">Nenhuma etiqueta cadastrada.</p>
        </div>
      ) : (
        <ResponsiveEntityList
          items={filtered}
          getKey={(label) => label.id}
          mobileTitle={(label) => (
            <span
              className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full text-white"
              style={{ backgroundColor: label.color ?? '#2563eb' }}
            >
              {label.name}
            </span>
          )}
          mobileFields={(label) => [
            { label: 'Descrição', value: label.description || '—' },
            {
              label: 'Status',
              value: (
                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                  label.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                }`}>
                  {label.isActive ? 'Ativa' : 'Inativa'}
                </span>
              ),
            },
          ]}
          columns={[
            {
              header: 'Etiqueta',
              cell: (label) => (
                <span
                  className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full text-white"
                  style={{ backgroundColor: label.color ?? '#2563eb' }}
                >
                  {label.name}
                </span>
              ),
            },
            { header: 'Descrição', cell: (label) => label.description || '—' },
            {
              header: 'Status',
              cell: (label) => (
                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                  label.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                }`}>
                  {label.isActive ? 'Ativa' : 'Inativa'}
                </span>
              ),
            },
          ]}
          actions={(label): ActionMenuItem[] => [
            {
              key: 'edit',
              label: 'Editar',
              onClick: () => openEditModal(label),
              icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              ),
            },
            {
              key: 'delete',
              label: 'Remover',
              variant: 'danger',
              onClick: () => {
                setLabelToDelete(label);
                setIsDeleteModalOpen(true);
              },
              icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              ),
            },
          ]}
        />
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingLabel ? 'Editar etiqueta' : 'Nova etiqueta'}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
            <input
              type="text"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
            <textarea
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              rows={2}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cor</label>
            <input
              type="color"
              value={form.color}
              onChange={e => setForm(f => ({ ...f, color: e.target.value }))}
              className="h-10 w-20 border border-gray-300 rounded-md"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))}
            />
            Etiqueta ativa
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>Cancelar</Button>
            <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Remover etiqueta"
      >
        <p className="text-sm text-gray-600 mb-4">
          Deseja remover a etiqueta <strong>{labelToDelete?.name}</strong>? Ela será desvinculada de todas as conversas.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)}>Cancelar</Button>
          <Button variant="danger" onClick={() => void handleDelete()}>Remover</Button>
        </div>
      </Modal>
    </div>
  );
};
